'use client';

import { motion } from 'motion/react';
import { useEffect, useMemo, useState } from 'react';

import * as StoreMap from '@/components/StoresMap';
import { useResponsiveness } from '@/hooks/useResponsiveness';

import { useSearchPageStore } from '../useSearchPageStore';

export type StoreResultMapProps = {
  content: OutletsMapContent;
  stores: Paginated<AdaptedStore<false, AdaptedNetwork>>;
};

export function StoresResultMap({ content, stores }: StoreResultMapProps) {
  const [hasBeenOpened, setHasBeenOpened] = useState(false);

  const { isMapOpen, toggleMap, isMapExpanded, toggleMapExpansion } =
    useSearchPageStore();

  const { isMobile } = useResponsiveness();

  useEffect(() => {
    if (isMobile) {
      toggleMap(true);
      toggleMapExpansion(false);
    }
  }, [isMobile]);

  useEffect(() => {
    if (!isMapExpanded) return;

    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [isMapExpanded]);

  useEffect(() => {
    if (isMapOpen && !hasBeenOpened) setHasBeenOpened(true);
  }, [isMapOpen, hasBeenOpened]);

  function onToggleExpandMap() {
    toggleMapExpansion();
  }

  function onToggleStoreMap() {
    toggleMap();
  }

  const animate = useMemo(() => {
    if (isMobile) return { width: '100vw' };

    return isMapOpen ? 'desktopVisible' : 'desktopHidden';
  }, [isMapOpen, isMobile]);

  return (
    <div className="lg:top-nav-bar-height fixed top-0 lg:sticky">
      <motion.div
        className="block overflow-hidden"
        animate={animate}
        transition={{ duration: 0.4, ease: 'easeInOut' }}
        variants={{
          desktopHidden: { height: '100%', width: '0', x: '100%', opacity: 0 },
          desktopVisible: {
            height: '100%',
            width: '50vw',
            x: '0',
            opacity: 1,
          },
        }}
      >
        {hasBeenOpened && (
          <StoreMap.Map
            content={content}
            inert={!isMapOpen}
            className="h-[80vh] w-screen lg:h-[calc(100vh-var(--nav-bar-height))] lg:w-[50vw]"
            stores={stores.items}
          >
            <StoreMap.Controls.Close
              onClick={onToggleStoreMap}
              className="hidden lg:block"
            />

            <StoreMap.Controls.Zoom />

            <StoreMap.Controls.Expand
              className="block lg:hidden"
              isExpanded={isMapExpanded}
              onClick={onToggleExpandMap}
            />
          </StoreMap.Map>
        )}
      </motion.div>
    </div>
  );
}
